import React from "react";
import cn from "./cn";

/**
 * Checkbox · Radio — the app's boolean and one-of-many controls.
 *
 * The control is wrapped in its `<label>` rather than bound by id, so the
 * association is structural: the text is clickable, the hit target is the whole
 * row, and no call site has to invent an id.
 *
 * Styling is `accent-color` from the brand token, which keeps the tick correct
 * in dark mode with no variant and preserves the native indeterminate state, the
 * platform focus ring and Windows high-contrast support — all of which a
 * redrawn `appearance-none` box gives up.
 *
 * Props:
 *   label        — the visible label. Omit only for a control in a table's
 *                  select column, and then pass `aria-label`.
 *   description  — a second line under the label
 *   size         — "sm" | "md"  (default "md")
 *   invalid      — error tone on the box
 *   variant      — "plain" (default) | "card": the whole label becomes a
 *                  bordered tile that takes the brand edge when checked — a
 *                  picker of a few options, each worth a sentence. The native
 *                  control stays visible inside it, so the tile is never the
 *                  only cue that it is a choice, or which one is chosen.
 */

const CHOICE_BOX_CLASSES =
  "tw-accent-primary tw-cursor-pointer tw-shrink-0 tw-m-0 " +
  "tw-focus-ring " +
  "disabled:tw-cursor-not-allowed disabled:tw-opacity-60";

const CHOICE_BOX_SIZE_CLASSES = { sm: "tw-w-3.5 tw-h-3.5", md: "tw-w-4 tw-h-4" };
const CHOICE_LABEL_SIZE_CLASSES = { sm: "tw-text-[0.8125rem]", md: "tw-text-sm" };

// `tw-border-solid` is load-bearing — Preflight is off, so a bare `tw-border`
// sets a width and no style, and paints nothing. `has-[:checked]` reads the
// control inside the label, so the tile cannot disagree with the input.
const CHOICE_CARD_CLASSES =
  "tw-flex tw-w-full tw-items-start tw-gap-3 tw-rounded-md tw-border tw-border-solid tw-border-line " +
  "tw-bg-surface-raised tw-p-3 tw-transition-colors " +
  "hover:tw-border-primary/50 has-[:checked]:tw-border-primary has-[:checked]:tw-bg-primary/[0.06]";

const CHOICE_VARIANTS = new Set(["plain", "card"]);
const warnedVariants = new Set();

const resolveVariant = (variant) => {
  if (CHOICE_VARIANTS.has(variant)) return variant;
  if (process.env.NODE_ENV !== "production" && !warnedVariants.has(variant)) {
    warnedVariants.add(variant);
    // eslint-disable-next-line no-console
    console.warn(`[Choice] unknown variant "${variant}" — falling back to "plain". Valid variants: plain, card.`);
  }
  return "plain";
};

const Choice = React.forwardRef(
  (
    {
      type,
      label,
      description,
      size = "md",
      invalid = false,
      disabled = false,
      variant = "plain",
      className = "",
      labelClassName = "",
      indeterminate,
      ...props
    },
    ref
  ) => {
    const card = resolveVariant(variant) === "card";
    // `indeterminate` is a DOM PROPERTY with no attribute: written as a prop it
    // reached the input as `indeterminate="false"` (and a React warning), and a
    // table's select-all never showed its half-ticked state.
    const inner = React.useRef(null);
    React.useImperativeHandle(ref, () => inner.current);
    React.useEffect(() => {
      // Untouched when not given: a page may set it through the ref itself.
      if (inner.current && indeterminate !== undefined) inner.current.indeterminate = !!indeterminate;
    }, [indeterminate]);
    const box = (
      <input
        ref={inner}
        type={type}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        className={cn(
          CHOICE_BOX_CLASSES,
          CHOICE_BOX_SIZE_CLASSES[size] || CHOICE_BOX_SIZE_CLASSES.md,
          type === "radio" ? "tw-rounded-full" : "tw-rounded-[3px]",
          invalid && "tw-outline tw-outline-1 tw-outline-danger",
          card && "tw-mt-0.5",
          !label && className
        )}
        {...props}
      />
    );

    // No label means a bare control — a table's select-all header, a grid cell.
    // The wrapper would only add a click target around nothing.
    if (!label) return box;

    return (
      <label
        className={cn(
          card ? CHOICE_CARD_CLASSES : "tw-inline-flex tw-items-start tw-gap-2",
          disabled ? "tw-cursor-not-allowed tw-opacity-70" : "tw-cursor-pointer",
          className
        )}
      >
        {box}
        <span className="tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5">
          <span
            className={cn(
              CHOICE_LABEL_SIZE_CLASSES[size] || CHOICE_LABEL_SIZE_CLASSES.md,
              "tw-text-content tw-leading-tight",
              labelClassName
            )}
          >
            {label}
          </span>
          {description && (
            <span className="tw-text-xs tw-leading-snug tw-text-content-subtle">
              {description}
            </span>
          )}
        </span>
      </label>
    );
  }
);
Choice.displayName = "Choice";

const Checkbox = React.forwardRef((props, ref) => (
  <Choice ref={ref} {...props} type="checkbox" />
));
Checkbox.displayName = "Checkbox";

const Radio = React.forwardRef((props, ref) => (
  <Choice ref={ref} {...props} type="radio" />
));
Radio.displayName = "Radio";

export { Checkbox, Radio };
